import { S } from './state.js';
import { Audio } from './audio.js';
import { ctx } from './canvas.js';
import { applyChill, damageEnemy, fireWeapon } from './combat.js';
import { BAL } from './config.js';
import { WEAPONS } from './data/weapons.js';
import { player } from './entities.js';
import { rrange } from './fx.js';
import { aim, joy } from './input.js';
import { keys, worldFromScreenX, worldFromScreenY } from './main.js';
import { COMPANIONS, COMPANION_IDX } from './economy.js';
import { cam } from './meter.js';
import { chainLightning, emitShock, popText, spawnParticle } from './procs.js';
import { hexA } from './render.js';
import { clampToWorld } from './world.js';
// ---- Dash ----
export function tryDash() {
  if (!S.started || S.dead || S.paused || S.shopOpen) return;
  if (player.dashStock <= 0) return;
  let dx = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
  let dy = (keys.down ? 1 : 0) - (keys.up ? 1 : 0);
  if (joy.id !== null && (joy.dx || joy.dy)) { dx = joy.dx; dy = joy.dy; }   // touch stick: dash where you're steering
  if (dx === 0 && dy === 0) {                       // not moving → dash toward the cursor
    const a = Math.atan2(worldFromScreenY(aim.y) - player.y, worldFromScreenX(aim.x) - player.x);
    dx = Math.cos(a); dy = Math.sin(a);
  } else { const l = Math.hypot(dx, dy); dx /= l; dy /= l; }
  const wasFull = player.dashStock >= S.DASH_CHARGES;
  player.dashStock--;
  if (S.items.breachcharge) emitShock(player.x, player.y, 100 + S.items.breachcharge * 20, 1, 2.4, 'breach');   // Breach Charge: the launch point detonates (120% dmg — emitShock's base is 50%, so scale 2.4)
  if (S.items.dashnova) emitShock(player.x, player.y, 110 + S.items.dashnova * 20, 1, 0.9, 'dashnova');   // Phase Nova
  if (S.items.frostdash) for (const e of S.enemies) { if (!e.dead && Math.hypot(e.x - player.x, e.y - player.y) < 150) { applyChill(e, 0.5, 120); damageEnemy(e, S.DAMAGE * 0.5, 1, 'blizzardstrike'); } }   // Glacial Dash
  if (S.items.voltdash) chainLightning(player.x, player.y, S.DAMAGE * 0.8, 4 + (S.items.voltdash - 1), 1, 'arc');   // Volt Dash
  if (S.items.dashshock) emitShock(player.x, player.y, 120 + S.items.dashshock * 20, 1, 0.8, 'dashnova');   // Shock Dash
  if (S.items.dashpoison && S.groundFires.length < BAL.econ.groundFireMax) S.groundFires.push({ x: player.x, y: player.y, r: 70 * S.RADIUS_MUL, life: 100, kind: 'toxic' });   // Toxic Trail
  if (S.items.dashfire) S.groundFires.push({ x: player.x, y: player.y, r: (55 + S.items.dashfire * 10) * S.RADIUS_MUL * S.GROUNDFIRE_MUL, life: 120, src: 'scorch' });   // Flame Trail
  if (wasFull) player.dashRe = S.DASH_RECHARGE;        // begin recharging from full
  const runSpeed = S.MAX_SPEED * 0.32 * S.fvSpeed;                          // uncapped top speed (normal movement clamps this at 13)
  const dashV = Math.max(S.DASH_SPEED, runSpeed * BAL.player.dashScale);  // dash reach scales with how fast you are
  if (S.items.blink) {   // Blink Matrix (mythic): the dash is a TELEPORT — through rocks, through bodies — and the departure rips foes inward
    const dist = Math.max(170, dashV * S.DASH_TIME * 0.55);
    const ox = player.x, oy = player.y;
    player.x += dx * dist; player.y += dy * dist;
    clampToWorld(player, player.r);
    player.dashing = 0; player.vx = dx * 4; player.vy = dy * 4;
    player.iFrames = Math.max(player.iFrames, S.DASH_TIME + 10);
    if (S.items.scorch) {   // Blink × Scorch: no dash frames means no trail — so the teleport burns BOTH ends instead
      for (const gp of [{ x: ox, y: oy }, { x: player.x, y: player.y }])
        S.groundFires.push({ x: gp.x, y: gp.y, r: (60 + S.items.scorch * 10) * S.RADIUS_MUL * S.GROUNDFIRE_MUL, life: 150 + S.items.scorch * 30, src: 'scorch' });
    }
    S.effects.push({ type: 'shock', x: ox, y: oy, r: 6, maxR: 200, life: 1 });
    for (const e of S.enemies)   // the rift at your departure point clusters the swarm for your AoE
      if (!e.dead && e.kind !== 'boss' && e.affix !== 'juggernaut' && Math.hypot(e.x - ox, e.y - oy) < 230) { e.x += (ox - e.x) * 0.45; e.y += (oy - e.y) * 0.45; }
    for (let k = 0; k < 12; k++) spawnParticle(player.x, player.y, 'gold');
    Audio.play('dash');
    return;
  }
  player.vx = dx * dashV; player.vy = dy * dashV;
  player.dashing = S.DASH_TIME;
  player.iFrames = Math.max(player.iFrames, S.DASH_TIME + 6);   // i-frames cover the whole burst — a real escape
  S.effects.push({ type: 'boostflash', x: player.x, y: player.y, life: 1 });
  if (S.EVO2 && S.EVO2.aurora) {   // Aurora: the dash collapses your orbital ring into freezing bursts
    for (const b of S.pbullets) if (b.orbit !== undefined && !b.collapsing) b.collapsing = true;
  }
  for (let k = 0; k < 10; k++) spawnParticle(player.x, player.y, 'normal');
  Audio.play('dash');
}

// ---- Companion (the optional menu-selected ally) ----
export function updateCompanion() {
  const c = S.companion; if (!c) return;
  c.ph += 0.045;                                    // gentle orbit + smoothing toward a point beside you
  const tx = player.x + Math.cos(c.ph) * 46, ty = player.y - 30 + Math.sin(c.ph) * 30;
  c.x += (tx - c.x) * 0.12; c.y += (ty - c.y) * 0.12;
  c.t++;
  if (c.key === 'drone') {                          // every ~1s, zap the nearest enemy for a little flat damage
    if (c.t % 60 === 0) {
      let best = null, bd = 420 * 420;
      for (const e of S.enemies) { if (e.dead) continue; const dd = (e.x - c.x) ** 2 + (e.y - c.y) ** 2; if (dd < bd) { bd = dd; best = e; } }
      if (best) {
        damageEnemy(best, 6 + S.DAMAGE * 0.18, 1, 'drone');    // depth 1 → flat hit, no crit/proc chains (stays "just a bit")
        S.effects.push({ type: 'spark', pts: [{ x: c.x, y: c.y }, { x: best.x, y: best.y }], life: 1 });
        if (S._sfxShoot > 0) { S._sfxShoot--; Audio.play('shoot'); }
      }
    }
  } else if (c.key === 'sprite') {                  // pre-magnetize nearby coins → effectively a wider pickup radius
    for (const co of S.coins) { if (!co.got && !co.mag && (co.x - player.x) ** 2 + (co.y - player.y) ** 2 < 280 * 280) co.mag = true; }
  } else if (c.key === 'mender') {                  // every ~10s, mend a sliver of health
    if (c.t % 600 === 0 && player.hp < S.MAX_HP) { player.hp = Math.min(S.MAX_HP, player.hp + Math.max(1, S.MAX_HP * 0.02)); popText({ x: player.x, y: player.y - 24 }, '+'); }
  } else if (c.key === 'bit') {                      // every ~12s, hand you a 1-point shield IF you're out — an occasional save
    if (c.t % 720 === 0 && player.shield <= 0) { player.shield = 1; S.effects.push({ type: 'boostflash', x: player.x, y: player.y, life: 1 }); }
  }
}
// ---- Mythic allies (0.16) ----
// Gemini Protocol: the weapon you swapped away from keeps firing from your hands at the nearest foe.
// 50% damage, half rate. Falls back to the trusty Blaster if you never swapped.
export function updateGemini() {
  if (!S.items.gemini || S.dead) return;
  if (--S.geminiCd > 0) return;
  const gw = WEAPONS[(S.lastWeapon >= 0 && S.lastWeapon !== S.weapon) ? S.lastWeapon : 0];
  let best = null, bd = (S.LASER_RANGE * 1.2) ** 2;
  for (const e of S.enemies) { if (e.dead) continue; const dd = (e.x - player.x) ** 2 + (e.y - player.y) ** 2; if (dd < bd) { bd = dd; best = e; } }
  if (!best) { S.geminiCd = 10; return; }
  fireWeapon(gw, Math.atan2(best.y - player.y, best.x - player.x), (S.DAMAGE + S.divBonus) * S.fvDmg * S.goldMult * S.spendMult * 0.5);
  S.geminiCd = Math.max(6, S.FIRE_COOLDOWN * S.fvFire * gw.cdMul * 2);
}
// Mirror Soul: a copy of you orbits wide and fires YOUR weapon at 35% power. fireWeapon emits from
// player.x/y, so we lend the mirror your coordinates for the synchronous call — beams/slams resolve
// inside it, bullets are born at the mirror's position. Hacky, contained, commented.
export function updateMirror() {
  if (!S.items.mirrorsoul || S.dead) { S.mirror = null; return; }
  if (!S.mirror) S.mirror = { x: player.x - 40, y: player.y - 40, ph: rrange(0, 6.28), cd: 30 };
  S.mirror.ph += 0.03;
  const tx = player.x + Math.cos(S.mirror.ph) * 70, ty = player.y + Math.sin(S.mirror.ph) * 70;
  S.mirror.x += (tx - S.mirror.x) * 0.1; S.mirror.y += (ty - S.mirror.y) * 0.1;
  if (--S.mirror.cd > 0) return;
  let best = null, bd = (S.LASER_RANGE * 1.1) ** 2;
  for (const e of S.enemies) { if (e.dead) continue; const dd = (e.x - S.mirror.x) ** 2 + (e.y - S.mirror.y) ** 2; if (dd < bd) { bd = dd; best = e; } }
  if (!best) { S.mirror.cd = 12; return; }
  const px = player.x, py = player.y;
  player.x = S.mirror.x; player.y = S.mirror.y;
  fireWeapon(WEAPONS[S.weapon], Math.atan2(best.y - S.mirror.y, best.x - S.mirror.x), (S.DAMAGE + S.divBonus) * S.fvDmg * S.goldMult * S.spendMult * 0.35);
  player.x = px; player.y = py;
  S.mirror.cd = Math.max(8, S.FIRE_COOLDOWN * S.fvFire * WEAPONS[S.weapon].cdMul * 1.6);
}
export function drawMirror() {
  if (!S.mirror) return;
  const x = S.mirror.x - cam.x, y = S.mirror.y - cam.y;
  ctx.save();
  ctx.globalAlpha = 0.55 + 0.15 * Math.sin(S.frame * 0.08);
  ctx.strokeStyle = '#8affec'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(x, y, 12, 0, Math.PI * 2); ctx.stroke();
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.font = '14px system-ui'; ctx.fillStyle = '#8affec'; ctx.fillText('🎭', x, y + 1);
  ctx.restore();
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
}
// Minefield (0.18): armed charges pulse until something steps close
export function drawMines() {
  for (const m of S.mines) {
    const x = m.x - cam.x, y = m.y - cam.y;
    if (x < -40 || x > S.W + 40 || y < -40 || y > S.H + 40) continue;
    const arming = m.arm > 0, pulse = 0.5 + 0.5 * Math.sin(S.frame * 0.15 + m.ph);
    ctx.beginPath(); ctx.arc(x, y, 7, 0, 6.2832);
    ctx.fillStyle = arming ? 'rgba(255,183,77,.25)' : `rgba(255,183,77,${0.35 + 0.3 * pulse})`; ctx.fill();
    ctx.strokeStyle = arming ? 'rgba(255,183,77,.4)' : `rgba(255,120,40,${0.5 + 0.4 * pulse})`; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.beginPath(); ctx.arc(x, y, 2.5, 0, 6.2832);
    ctx.fillStyle = arming ? '#caa46a' : (pulse > 0.5 ? '#ff5d4a' : '#ffd24a'); ctx.fill();
  }
}
export function drawCompanion() {
  const c = S.companion; if (!c) return;
  const info = COMPANIONS[COMPANION_IDX[c.key]];
  const x = c.x - cam.x, y = c.y - cam.y;   // world → screen, like every other draw fn
  ctx.save();
  ctx.beginPath(); ctx.arc(x, y, 11, 0, 6.2832);
  ctx.fillStyle = hexA(info.color, 0.18); ctx.fill();
  ctx.strokeStyle = hexA(info.color, 0.8); ctx.lineWidth = 1.5; ctx.stroke();
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.font = '15px system-ui'; ctx.fillText(info.icon, x, y + 1);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.restore();
}
